import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { apiFetch } from '../../api/client.js';
import { useAuth } from '../../auth/useAuth.js';
import { formatTransactionAmount as transactionMoney } from '../../utils/transaction-money.js';

import { InboundReviewPage, SourceStatus } from './InboundReviewPage.js';
import { PaymentsPage } from './PaymentsPage.js';
import { RecordManualPaymentDialog } from './RecordManualPaymentDialog.js';
import { RecordManualTransferDialog } from './RecordManualTransferDialog.js';
import { WithdrawalsPage } from './WithdrawalsPage.js';
import { APP_LOCALE } from '../../utils/locale.js';

export interface TransactionRow {
  id: string;
  source: string;
  customerId: string;
  customerName: string;
  direction: string;
  paymentType: string;
  amountMinor: string;
  currency: string;
  status: string;
  counterpartyName: string;
  counterpartyAccount: string;
  reference: string;
  createdAt: string;
  /** Last change to the record (DEV-94); older sources may not send it. */
  updatedAt?: string;
  provider: string;
  senderAccount: string;
  recipientAccount: string;
  /** Set when the other side is a Norvix client too: link to their profile. */
  counterpartyCustomerId?: string;
  counterpartyCustomerName?: string;
  feeName?: string;
  feeAccountId?: string;
  ledgerReference?: string;
}
interface Page {
  items: TransactionRow[];
  nextCursor: string;
  asOf: string;
  customer?: { id: string; name: string } | null;
}
const TYPES = [
  ['INBOUND_TRANSFER', 'Incoming transfer'],
  ['SEPA_CT', 'SEPA transfer'],
  ['SEPA_INSTANT', 'SEPA Instant'],
  ['SWIFT', 'SWIFT'],
  ['WITHDRAWAL', 'Manual withdrawal'],
  ['FEE_CHARGE', 'Commission'],
  ['FEE_REFUND', 'Commission refund'],
];
const STATUSES = [
  ['POSTED', 'Posted'],
  ['AWAITING_RELEASE', 'Awaiting release'],
  ['UNATTRIBUTED', 'Needs attribution'],
  ['AWAITING_DECISION', 'Awaiting decision'],
  ['AWAITING_BANK', 'Awaiting bank transfer'],
  ['CREDITED', 'Credited'],
  ['PROCESSING', 'Processing'],
  ['SETTLED', 'Settled'],
  ['SENT', 'Sent at bank'],
  ['REJECTED', 'Rejected'],
  ['FAILED', 'Failed'],
  ['BLOCKED', 'Blocked'],
  ['RETURNED', 'Returned'],
  ['RECALLED', 'Recalled'],
  ['RECALL_PENDING', 'Recall pending'],
];
function channelLabel(provider: string): string {
  const labels: Record<string, string> = {
    GURUPAY: 'GuruPay',
    GURUPAY_VIBAN: 'GuruPay',
    BANKERA: 'Bankera',
    BANKERA_SHARED: 'Bankera',
    IFX: 'IFX',
    IFX_SHARED: 'IFX',
    MANUAL_SHARED: 'Manual bank',
    IBANQ: 'IBANQ',
    IBANQ_SUBACCOUNT: 'IBANQ',
    IBANQ_SHARED: 'IBANQ',
    NORVIX: 'Norvix',
  };
  return labels[provider] ?? (provider || 'Channel not recorded');
}
function statusLabel(row: TransactionRow): string {
  if (row.source === 'FEE') return 'Posted';
  if (row.source === 'INBOUND')
    return (
      (
        {
          PENDING_REVIEW: 'Awaiting release',
          UNATTRIBUTED: 'Needs attribution',
          APPROVING: 'Releasing',
          APPROVED: 'Credited',
          EVIDENCE_ONLY: 'Excluded — bank evidence only',
        } as Record<string, string>
      )[row.status] ?? row.status
    );
  if (row.source === 'WITHDRAWAL')
    return (
      (
        {
          REQUESTED: 'Awaiting decision',
          APPROVED: 'Awaiting bank transfer',
          SUBMITTING: 'Submitting to bank',
          SUBMITTED: 'Processing at bank',
          EXECUTION_UNKNOWN: 'Bank reconciliation required',
          APPROVAL_UNKNOWN: 'Ledger reconciliation required',
          REFUNDING: 'Refund being reconciled',
          BANK_REJECTED: 'Bank rejected — refund required',
          SENT: 'Sent at bank',
        } as Record<string, string>
      )[row.status] ?? row.status
    );
  return row.status === 'PENDING'
    ? 'Awaiting decision'
    : row.status.replaceAll('_', ' ').toLowerCase();
}
function color(row: TransactionRow): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (['PENDING', 'PENDING_REVIEW', 'UNATTRIBUTED', 'REQUESTED', 'BLOCKED'].includes(row.status))
    return 'warning';
  if (['FAILED', 'REJECTED', 'RETURNED'].includes(row.status)) return 'error';
  if (row.status === 'POSTED' || row.status === 'SETTLED' || (row.source === 'INBOUND' && row.status === 'APPROVED'))
    return 'success';
  if (['APPROVED', 'PROCESSING', 'ACCEPTED', 'APPROVING', 'SENT'].includes(row.status))
    return 'info';
  return 'default';
}

export function TransactionsPage(): JSX.Element {
  const { user, can } = useAuth();
  // Finance reads a summary of each operation; the payment roles open the full review (DEV-67).
  const monitoringOnly = !can('payments.workspace');
  const viewer = `${user?.id ?? ''}:${user?.role ?? ''}`;
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const pageParameter = Number(params.get('page') ?? '1');
  const page =
    Number.isInteger(pageParameter) && pageParameter > 0 && pageParameter < 1_000_000
      ? pageParameter - 1
      : 0;
  const [cursors, setCursors] = useState<Record<number, string>>({
    0: '',
    [page]: params.get('cursor') ?? '',
  });
  const [recording, setRecording] = useState(false);
  const [recordingOutgoing, setRecordingOutgoing] = useState(false);
  const [outcome, setOutcome] = useState('');
  const filters = new URLSearchParams();
  for (const name of [
    'search',
    'customerId',
    'direction',
    'type',
    'status',
    'currency',
    'from',
    'through',
    'queue',
    'source',
    'limit',
    'includeFees',
    'sort',
  ]) {
    const value = params.get(name);
    if (value) filters.set(name, value);
  }
  const filterKey = filters.toString();
  const byActivity = params.get('sort') === 'ACTIVITY';
  // Opens by itself when one of its filters is in use, so nothing active is hidden.
  const hiddenFilterSet = ['type', 'currency', 'from', 'through'].some((name) => params.get(name));
  const [showMore, setShowMore] = useState(false);
  const moreFilters = showMore || hiddenFilterSet;
  const activeFilterCount = [...filters.keys()].filter((name) => name !== 'limit').length;
  const [previousFilters, setPreviousFilters] = useState(filterKey);
  // Reset before rendering the query; an old cursor must never be sent with new filters.
  if (previousFilters !== filterKey) {
    setPreviousFilters(filterKey);
    setCursors({ 0: '', [page]: params.get('cursor') ?? '' });
  }
  const currentCursor = params.get('cursor') ?? '';
  if (currentCursor) filters.set('cursor', currentCursor);
  const query = useQuery({
    queryKey: ['transactions', viewer, filterKey, currentCursor],
    queryFn: () => apiFetch<Page>(`/admin/transactions?${filters.toString()}`),
  });
  const setFilter = useCallback(
    (name: string, value: string): void => {
      const next = new URLSearchParams(params);
      value ? next.set(name, value) : next.delete(name);
      next.delete('id');
      next.delete('detailSource');
      next.delete('cursor');
      next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const urlSearch = params.get('search') ?? '';
  useEffect(() => {
    setSearch(urlSearch);
  }, [urlSearch]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (search !== urlSearch) setFilter('search', search);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search, urlSearch, setFilter]);
  const selectedId = params.get('id');
  const selectedSource = params.get('detailSource');
  const swiftDetail = useQuery({
    queryKey: ['transaction-detail', viewer, 'SWIFT', selectedId],
    queryFn: () => apiFetch<TransactionRow>(`/admin/transactions/SWIFT/${selectedId ?? ''}`),
    enabled: !monitoringOnly && selectedSource === 'SWIFT' && !!selectedId,
  });
  const swift = swiftDetail.data;
  const feeDetail = useQuery({
    queryKey: ['transaction-detail', viewer, 'FEE', selectedId],
    queryFn: () => apiFetch<TransactionRow>(`/admin/transactions/FEE/${selectedId ?? ''}`),
    enabled: !monitoringOnly && selectedSource === 'FEE' && !!selectedId,
  });
  const fee = feeDetail.data;
  const inboundAccounts = useQuery({
    queryKey: ['transaction-accounts', viewer, 'INBOUND', selectedId],
    queryFn: () => apiFetch<TransactionRow>(`/admin/transactions/INBOUND/${selectedId ?? ''}`),
    enabled: !monitoringOnly && selectedSource === 'INBOUND' && !!selectedId,
  });
  const financeDetail = useQuery({
    queryKey: ['transaction-detail', viewer, selectedSource, selectedId],
    queryFn: () => apiFetch<TransactionRow>(`/admin/transactions/${encodeURIComponent(selectedSource ?? '')}/${encodeURIComponent(selectedId ?? '')}`),
    enabled: monitoringOnly && !!selectedId && ['INBOUND', 'WITHDRAWAL', 'PAYMENT', 'SWIFT', 'FEE'].includes(selectedSource ?? ''),
  });
  const close = (): void => {
    const next = new URLSearchParams(params);
    next.delete('id');
    next.delete('detailSource');
    setParams(next, { replace: true });
  };
  const show = (row: TransactionRow): void => {
    const next = new URLSearchParams(params);
    next.set('id', row.id);
    next.set('detailSource', row.source);
    setParams(next, { replace: true });
  };
  const refresh = (): void => {
    const next = new URLSearchParams(params);
    next.delete('cursor');
    next.delete('page');
    setParams(next, { replace: true });
    setCursors({ 0: '' });
    void client.invalidateQueries({ queryKey: ['transactions'] });
  };
  const goToPage = (index: number, cursor: string): void => {
    const next = new URLSearchParams(params);
    if (index === 0) {
      next.delete('page');
      next.delete('cursor');
    } else {
      next.set('page', String(index + 1));
      next.set('cursor', cursor);
    }
    setCursors({ ...cursors, [page]: currentCursor, [index]: cursor });
    setParams(next);
  };
  const selector = (name: string, label: string, choices: string[][]): JSX.Element => (
    <TextField
      select
      size="small"
      label={label}
      value={params.get(name) ?? ''}
      onChange={(event) => setFilter(name, event.target.value)}
      fullWidth
    >
      <MenuItem value="">
        {label === 'Status' ? 'All statuses' : `All ${label.toLowerCase()}s`}
      </MenuItem>
      {choices.map(([value, title]) => (
        <MenuItem key={value} value={value}>
          {title}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
      >
        <Box>
          <Typography variant="h4">Transactions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Review money in and out, from arrival through the final recorded status.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} disabled={query.isFetching} onClick={refresh}>
            Refresh
          </Button>
          {can('payments.review') && <Button variant="outlined" onClick={() => setRecording(true)}>
            Record incoming payment
          </Button>}
          {/* Both directions, side by side: staff read one bank statement and
              type what is on it, and until now only half of it could be
              entered. */}
          {can('payments.send') && <Button variant="outlined" onClick={() => setRecordingOutgoing(true)}>
            Record outgoing transfer
          </Button>}
        </Stack>
      </Stack>
      {monitoringOnly && <Alert severity="info">Finance monitoring · Read-only access. Account numbers are masked.</Alert>}
      <Tabs
        value={params.get('queue') ?? ''}
        onChange={(_, value: string) => setFilter('queue', value)}
        aria-label="Transaction queues"
        variant="scrollable"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="" label="All transactions" />
        <Tab value="REVIEW" label="Needs review" />
        <Tab value="BANK" label="Awaiting bank transfer" />
      </Tabs>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
              xl: '2fr repeat(4, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          <TextField
            size="small"
            label="Search transactions"
            placeholder="Customer, counterparty, IBAN, reference or ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            inputProps={{ maxLength: 120 }}
          />
          {selector('status', 'Status', STATUSES)}
          {selector('direction', 'Movement', [
            ['IN', 'Incoming / Credit'],
            ['OUT', 'Outgoing / Debit'],
            ['COMMISSION', 'Commission / Debit'],
            ['COMMISSION_REFUND', 'Commission refund / Credit'],
          ])}
          {/* Newest first by default. Latest activity (DEV-94) on request: a
              provider poll touches every row it reads, so by default that
              order put weeks-old payments on top. */}
          <TextField
            select
            size="small"
            label="Order"
            value={byActivity ? 'ACTIVITY' : ''}
            onChange={(event) => setFilter('sort', event.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Newest first</MenuItem>
            <MenuItem value="ACTIVITY">Latest activity</MenuItem>
          </TextField>
        </Box>
        {/* The rarely used filters, out of the way until wanted. */}
        {moreFilters ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
              mt: 2,
            }}
          >
            {selector('type', 'Type', TYPES)}
            <TextField
              size="small"
              label="Currency"
              placeholder="All currencies"
              value={params.get('currency') ?? ''}
              onChange={(event) => setFilter('currency', event.target.value.toUpperCase())}
              inputProps={{ maxLength: 3 }}
            />
            <TextField
              size="small"
              type="date"
              label="Created from (UTC)"
              value={params.get('from') ?? ''}
              onChange={(event) => setFilter('from', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="Created through (UTC)"
              value={params.get('through') ?? ''}
              onChange={(event) => setFilter('through', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        ) : null}
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Button
            size="small"
            aria-expanded={moreFilters}
            disabled={moreFilters && hiddenFilterSet}
            onClick={() => setShowMore((open) => !open)}
          >
            {moreFilters ? 'Fewer filters' : 'More filters'}
          </Button>
          {params.get('source') && (
            <Chip
              label={`Source: ${params.get('source')}`}
              onDelete={() => setFilter('source', '')}
            />
          )}
          {params.get('customerId') && (
            <Chip
              label={`Customer: ${query.data?.customer?.name || params.get('customerId')}`}
              onDelete={() => setFilter('customerId', '')}
              sx={{ maxWidth: 320 }}
            />
          )}
          <Button
            onClick={() => {
              setSearch('');
              const reset = new URLSearchParams();
              const customerId = params.get('customerId');
              if (customerId) reset.set('customerId', customerId);
              setParams(reset, { replace: true });
            }}
          >
            Clear filters
          </Button>
          <FormControlLabel label="Show fees" control={<Switch
            checked={params.get('includeFees') !== 'false'}
            onChange={(_, checked) => setFilter('includeFees', checked ? '' : 'false')}
          />} />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {activeFilterCount ? `${activeFilterCount} active filters · ` : ''}
            {query.data
              ? `Loaded ${new Date(query.data.asOf).toLocaleString(APP_LOCALE)}`
              : 'Full transaction history'}
          </Typography>
        </Stack>
      </Paper>
      {outcome && (
        <Alert severity="success" onClose={() => setOutcome('')}>
          {outcome}
        </Alert>
      )}
      {!monitoringOnly && <SourceStatus />}
      {query.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}
        >
          {query.error instanceof Error ? query.error.message : 'Transactions could not be loaded.'}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1050 }} aria-label="Transactions">
            <TableHead>
              <TableRow>
                {[
                  byActivity ? 'Transaction / latest activity' : 'Transaction / created',
                  'Customer',
                  'Counterparty',
                  'Movement',
                  'Amount',
                  'Status',
                  '',
                ].map((heading, index) => (
                  <TableCell key={index} align={heading === 'Amount' ? 'right' : 'left'}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isPending ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} aria-label="Loading transactions" />
                  </TableCell>
                </TableRow>
              ) : !query.isError && query.data?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    No transactions match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                query.data?.items.map((row) => (
                  <TableRow key={`${row.source}:${row.id}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.status === 'EVIDENCE_ONLY' ? 'Excluded bank record' : row.source === 'WITHDRAWAL' && row.provider === 'GURUPAY_VIBAN' ? 'Outgoing transfer' : row.feeName || TYPES.find(([value]) => value === row.paymentType)?.[1] || row.paymentType}
                      </Typography>
                      {row.source === 'FEE' && <Typography variant="caption" color="text.secondary" display="block">
                        {row.direction === 'IN' ? 'Commission refund' : 'Commission'}
                      </Typography>}
                      {byActivity && row.updatedAt && row.updatedAt !== row.createdAt ? (
                        <>
                          <Typography variant="caption" display="block">
                            Updated {new Date(row.updatedAt).toLocaleString(APP_LOCALE)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Created {new Date(row.createdAt).toLocaleString(APP_LOCALE)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(row.createdAt).toLocaleString(APP_LOCALE)}
                        </Typography>
                      )}
                      <Typography variant="caption" title={row.id}>
                        {row.id.slice(0, 8)}…
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {channelLabel(row.provider)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      {row.customerId && monitoringOnly ? <Typography variant="body2">{row.customerName || row.customerId}</Typography> : row.customerId ? (
                        <Tooltip title={row.customerName || row.customerId} describeChild>
                          <MuiLink
                            component={Link}
                            to={`/customers/${row.customerId}`}
                            color="inherit"
                            underline="hover"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 600,
                            }}
                          >
                            {row.customerName || row.customerId.slice(0, 8)}
                          </MuiLink>
                        </Tooltip>
                      ) : (
                        <Chip size="small" color="warning" label="Unattributed" />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 265, maxWidth: 320 }}>
                      {row.counterpartyCustomerId && !monitoringOnly ? (
                        <MuiLink
                          component={Link}
                          to={`/customers/${row.counterpartyCustomerId}`}
                          color="inherit"
                          underline="hover"
                          title={`Norvix client: ${row.counterpartyCustomerName || row.counterpartyName}`}
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}
                        >
                          {row.counterpartyName || row.counterpartyCustomerName || 'Norvix client'}
                        </MuiLink>
                      ) : (
                        <Typography variant="body2" noWrap title={row.counterpartyName}>
                          {row.counterpartyName || 'Not supplied'}
                        </Typography>
                      )}
                      {row.counterpartyCustomerId ? (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          Norvix client{row.counterpartyCustomerName && row.counterpartyCustomerName !== row.counterpartyName ? ` · ${row.counterpartyCustomerName}` : ''}
                        </Typography>
                      ) : null}
                      {row.source === 'FEE' ? <Typography variant="caption" color="text.secondary" display="block" noWrap title={row.feeAccountId}>
                        Customer account: {row.feeAccountId || 'Not recorded'}
                      </Typography> : <><Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        From: {row.senderAccount || 'Not recorded'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        To: {row.recipientAccount || 'Not recorded'}
                      </Typography></>}
                      <Typography variant="caption" noWrap display="block" title={row.reference}>
                        {row.reference || 'No reference'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        color={row.status !== 'EVIDENCE_ONLY' && row.direction === 'IN' ? 'success.main' : 'text.secondary'}
                      >
                        {row.source === 'FEE' || row.status === 'EVIDENCE_ONLY' ? null : row.direction === 'IN' ? (
                          <CallReceivedIcon fontSize="small" />
                        ) : (
                          <CallMadeIcon fontSize="small" />
                        )}
                        <Box><Typography variant="body2">
                          {row.status === 'EVIDENCE_ONLY' ? 'Bank evidence' : row.source === 'FEE' ? (row.direction === 'IN' ? 'Commission refund' : 'Commission') : row.direction === 'IN' ? 'Incoming' : 'Outgoing'}
                        </Typography><Typography variant="caption" color="text.secondary">{row.status === 'EVIDENCE_ONLY' ? 'No posting' : row.direction === 'IN' ? 'Credit' : 'Debit'}</Typography></Box>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 600,
                      }}
                    >
                      {transactionMoney(row.amountMinor, row.currency)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={color(row)}
                        label={statusLabel(row)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => show(row)}
                        aria-label={`${monitoringOnly ? 'View transaction' : row.source === 'FEE' ? 'View fee' : row.status === 'EVIDENCE_ONLY' ? 'View bank evidence' : 'Review transaction'} ${row.id}`}
                      >
                        {monitoringOnly || row.source === 'FEE' || row.status === 'EVIDENCE_ONLY' ? 'Details' : 'Review'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="body2" color="text.secondary">
            Page {page + 1} · {query.data?.items.length ?? 0} transactions
          </Typography>
          <TextField
            select
            size="small"
            label="Rows"
            value={params.get('limit') ?? '25'}
            onChange={(event) => setFilter('limit', event.target.value)}
            sx={{ width: 90, ml: 'auto' }}
          >
            {[25, 50, 100].map((value) => (
              <MenuItem key={value} value={String(value)}>
                {value}
              </MenuItem>
            ))}
          </TextField>
          <Button
            disabled={page === 0 || query.isFetching}
            onClick={() =>
              goToPage(cursors[page - 1] === undefined ? 0 : page - 1, cursors[page - 1] ?? '')
            }
          >
            {page > 0 && cursors[page - 1] === undefined ? 'First page' : 'Previous'}
          </Button>
          <Button
            disabled={!query.data?.nextCursor || query.isFetching || query.isError}
            onClick={() => {
              if (query.data?.nextCursor) {
                goToPage(page + 1, query.data.nextCursor);
              }
            }}
          >
            Next
          </Button>
        </Stack>
      </Paper>
      {!monitoringOnly && selectedId && selectedSource === 'PAYMENT' && (
        <PaymentsPage key={selectedId} reviewId={selectedId} onReviewClose={close} />
      )}
      {!monitoringOnly && selectedId && selectedSource === 'INBOUND' && (
        <InboundReviewPage
          key={selectedId}
          reviewId={selectedId}
          onReviewClose={close}
          reviewAccounts={inboundAccounts.data}
          accountsUnavailable={inboundAccounts.isError}
        />
      )}
      {!monitoringOnly && selectedId && selectedSource === 'WITHDRAWAL' && (
        <WithdrawalsPage key={selectedId} reviewId={selectedId} onReviewClose={close} />
      )}
      <Drawer
        anchor="right"
        open={!monitoringOnly && selectedSource === 'SWIFT' && !!selectedId}
        onClose={close}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, maxWidth: '100vw', p: 3 } }}
      >
        {swiftDetail.isPending && <CircularProgress aria-label="Loading SWIFT transaction" />}
        {swiftDetail.isError && (
          <Alert severity="error">
            Transaction details could not be loaded.<Button onClick={close}>Close</Button>
          </Alert>
        )}
        {swift && (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h5">SWIFT transaction</Typography>
              <Button onClick={close}>Close</Button>
            </Stack>
            <Typography>{transactionMoney(swift.amountMinor, swift.currency)}</Typography>
            <Typography>{swift.counterpartyName}</Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }}>
              From: {swift.senderAccount || 'Not recorded'}
            </Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }}>
              To: {swift.recipientAccount || 'Not recorded'}
            </Typography>
            <Typography variant="body2">Channel: {channelLabel(swift.provider)}</Typography>
            <Typography variant="body2">
              Verification of Payee: no provider result recorded
            </Typography>
            <Typography>{swift.reference || 'No reference'}</Typography>
            <Chip label={statusLabel(swift)} />
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              Transaction ID: {swift.id}
            </Typography>
            <Alert severity="info">
              This transfer follows the SWIFT provider workflow. Its recorded status does not permit
              manual settlement from this screen.
            </Alert>
            {swift.customerId && (
              <Button component={Link} to={`/customers/${swift.customerId}`}>
                View customer
              </Button>
            )}
          </Stack>
        )}
      </Drawer>
      <Drawer anchor="right" open={!monitoringOnly && selectedSource === 'FEE' && !!selectedId} onClose={close}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, maxWidth: '100vw', p: 3 } }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h5">{fee?.direction === 'IN' ? 'Commission refund' : 'Commission'}</Typography>
            <Button onClick={close}>Close</Button>
          </Stack>
          {feeDetail.isPending && <CircularProgress aria-label="Loading fee details" />}
          {feeDetail.isError && <Alert severity="error">Fee details could not be loaded.</Alert>}
          {fee && <>
            <Typography variant="h6">{fee.feeName}</Typography>
            <Typography variant="h5">{fee.direction === 'IN' ? '+' : '−'}{transactionMoney(fee.amountMinor, fee.currency)}</Typography>
            <Chip label="Posted" color="success" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
            <Typography variant="body2">Posted: {new Date(fee.createdAt).toLocaleString(APP_LOCALE)}</Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Customer account: {fee.feeAccountId}</Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Ledger entry: {fee.id}</Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Reference: {fee.ledgerReference}</Typography>
            <Typography variant="body2" color="text.secondary">
              {fee.direction === 'IN' ? 'This fee refund has been credited to the customer’s account.' : 'This fee has been debited from the customer’s account.'}
            </Typography>
            <Button component={Link} to={`/customers/${fee.customerId}`}>View customer</Button>
          </>}
        </Stack>
      </Drawer>
      <Drawer anchor="right" open={monitoringOnly && !!selectedId} onClose={close}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, maxWidth: '100vw', p: 3 } }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h5">Transaction details</Typography>
            <Button onClick={close}>Close</Button>
          </Stack>
          <Alert severity="info">Read-only finance view · Account numbers are masked.</Alert>
          {financeDetail.isFetching && <CircularProgress aria-label="Loading transaction details" />}
          {financeDetail.isError && <Alert severity="error" action={<Button onClick={() => void financeDetail.refetch()}>Retry</Button>}>Transaction details could not be loaded.</Alert>}
          {financeDetail.data && <>
            <Typography variant="h5">{transactionMoney(financeDetail.data.amountMinor, financeDetail.data.currency)}</Typography>
            <Chip label={statusLabel(financeDetail.data)} color={color(financeDetail.data)} sx={{ alignSelf: 'flex-start' }} />
            {Object.entries({
              'Transaction ID': financeDetail.data.id,
              Created: new Date(financeDetail.data.createdAt).toLocaleString(APP_LOCALE),
              Customer: financeDetail.data.customerName || financeDetail.data.customerId,
              Counterparty: financeDetail.data.counterpartyName,
              Movement: financeDetail.data.direction === 'IN' ? 'Incoming / Credit' : 'Outgoing / Debit',
              Type: financeDetail.data.feeName || TYPES.find(([value]) => value === financeDetail.data?.paymentType)?.[1] || financeDetail.data.paymentType,
              Channel: channelLabel(financeDetail.data.provider),
              From: financeDetail.data.senderAccount,
              To: financeDetail.data.recipientAccount,
              Reference: financeDetail.data.reference || financeDetail.data.ledgerReference,
            }).map(([label, value]) => <Box key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value || 'Not recorded'}</Typography>
            </Box>)}
          </>}
        </Stack>
      </Drawer>
      {can('payments.review') && <RecordManualPaymentDialog
        open={recording}
        onClose={() => setRecording(false)}
        onRecorded={(message) => {
          setRecording(false);
          setOutcome(message);
          refresh();
        }}
      />}
      {can('payments.send') && <RecordManualTransferDialog
        open={recordingOutgoing}
        onClose={() => setRecordingOutgoing(false)}
        onRecorded={(message) => {
          setRecordingOutgoing(false);
          setOutcome(message);
          refresh();
        }}
      />}
    </Stack>
  );
}
